import { useEffect, useRef, useState, type JSX } from 'react';

import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';

import { searchPlaces } from '@/api/services/placesService';
import type { PlaceSummary } from '@/api/types/Place';
import { PlaceTypeChip } from '@/pages/orders/places/PlaceTypeChip';
import { formatPlaceCopy } from '@/utils/placeNote';
import { useAppSelector } from '@/store/hooks';
import { selectPlacesRevision } from '@/store/placesSlice';

// Adresář našeptávač for the jízda editor's Nakládka / Vykládka row
// (`prototyp.html#s-editor-ac`, contract §E).
//
// Deliberately NOT a 5th `RegistryKind`: `RegistryAutocomplete` synthesises a
// „Vytvořit …" option unconditionally (RULE-05 auto-create) and its value shape
// is `{id, name}` — but this field must never create a place, and the caller
// needs the WHOLE `PlaceSummary` back because the place's note is appended to
// „Poznámka pro dispečera". Suppressing the create option there would be a
// behavioural fork in code four live call sites depend on. The proven internals
// (250 ms debounce, `reqIdRef` stale-response guard, `freeSolo`, the DESIGN.md
// paper `slotProps`) are copied; blast radius on the existing four kinds: zero.
//
// The jízda field itself stays FREE TEXT — selecting a place only fills the
// text („Název — Adresa"); nothing is linked, nothing is migrated.

const DEBOUNCE_MS = 250;
const OPTION_ADDRESS_FONT_SIZE = 12;

export interface PlaceAutocompleteProps {
  // The field text as the form owns it (plain string — no FK, no id).
  value: string;
  // Fires on every keystroke AND on select (with the formatted insert text).
  onChange: (text: string) => void;
  // Fires ONLY when a registry row was actually picked — carries `note`, which
  // the editor appends to „Poznámka pro dispečera" (§8).
  onPlaceSelected: (place: PlaceSummary) => void;
  // Filters server-side INCLUSIVELY of `BOTH` (a „Obojí" place is offered by
  // both fields).
  placeType: 'LOADING' | 'UNLOADING';
  label: string;
  // Locks the field for a dispatcher / read-only jízda (RBAC matrix).
  disabled?: boolean;
  inputSx?: SxProps<Theme>;
  'data-testid'?: string;
}

export const PlaceAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  placeType,
  label,
  disabled,
  inputSx,
  'data-testid': dataTestId,
}: PlaceAutocompleteProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  // Bumped by every place mutation (`placesSlice`) — a place created in the
  // Adresář dialog must be offered here without a page reload.
  const revision = useAppSelector(selectPlacesRevision);

  const [input, setInput] = useState(value);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<PlaceSummary[]>([]);
  const [loading, setLoading] = useState(false);
  // Track the latest in-flight query so a stale slow response doesn't clobber
  // a fast fresh one (typing-race correctness).
  const reqIdRef = useRef(0);

  // Keep the input synced with the externally controlled value (dialog reopen,
  // load of another jízda, …).
  useEffect(() => {
    setInput(value);
  }, [value]);

  // A mutation elsewhere invalidates whatever this dropdown cached.
  useEffect(() => {
    setOptions([]);
  }, [revision]);

  // Debounced fetch — re-run on input, open transition, type and revision.
  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      searchPlaces(input, placeType)
        .then((found) => {
          if (reqId !== reqIdRef.current) return;
          setOptions(found);
        })
        .catch(() => {
          // Offline / 403 — the field degrades to a plain free-text input.
          if (reqId !== reqIdRef.current) return;
          setOptions([]);
        })
        .finally(() => {
          if (reqId !== reqIdRef.current) return;
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [input, open, placeType, revision]);

  return (
    <Autocomplete<PlaceSummary, false, false, true>
      freeSolo
      fullWidth
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      // The api already filtered (name ∨ adresa ∨ kontakt ∨ poznámka) — a
      // client-side second pass would hide rows matched on contact/note.
      filterOptions={(opts) => opts}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : formatPlaceCopy(opt))}
      isOptionEqualToValue={(opt, val) =>
        typeof val === 'string' ? formatPlaceCopy(opt) === val : opt.id === val.id
      }
      value={input}
      onChange={(_, next) => {
        if (next === null) {
          onChange('');
          return;
        }
        if (typeof next === 'string') {
          // freeSolo Enter without a highlighted option — keep what was typed.
          onChange(next);
          return;
        }
        const text = formatPlaceCopy(next);
        setInput(text);
        onChange(text);
        onPlaceSelected(next);
      }}
      inputValue={input}
      onInputChange={(_, val, reason) => {
        // `reset` is MUI echoing the value back after a selection — the select
        // branch above already wrote the formatted text.
        if (reason === 'reset') return;
        setInput(val);
        onChange(val);
      }}
      loading={loading}
      // MUI renders this while `loading && options.length === 0` (freeSolo
      // included); without it the Czech-only UI shows MUI's English default.
      loadingText={t('common:registry.loading')}
      // Vapas dropdown surface (DESIGN.md §6.5 / §6.3): surface bg, 12px radius
      // (card/modal scale), border, resting card shadow — identical to
      // `RegistryAutocomplete` so the editor reads as one system.
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.elevation.shadow2,
            '& .MuiAutocomplete-option': { fontSize: 13.5, minHeight: 44 },
          },
        },
      }}
      // Option row (`#s-editor-ac`): pin icon · bold name over grey address
      // subline · type chip pushed right. NO „Vytvořit …" row — ever.
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key?: string };
        const address = option.address?.trim() ?? '';
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{ gap: 1, alignItems: 'center' }}
            data-testid="place-option"
          >
            <PlaceIcon
              sx={{ fontSize: 16, color: theme.palette.text.secondary, flexShrink: 0 }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                lineHeight: 1.25,
                minWidth: 0,
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }} data-testid="place-option-name">
                {option.name}
              </Box>
              {address.length > 0 ? (
                <Box
                  component="span"
                  sx={{
                    fontSize: OPTION_ADDRESS_FONT_SIZE,
                    color: theme.palette.text.secondary,
                  }}
                  data-testid="place-option-address"
                >
                  {address}
                </Box>
              ) : null}
            </Box>
            <Box sx={{ ml: 'auto', pl: 1 }}>
              <PlaceTypeChip type={option.type} data-testid="place-option-type" />
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          data-testid={dataTestId}
          InputProps={{
            ...params.InputProps,
            sx: inputSx,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="primary" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
